/**
 * Wall-clock timings per stage.
 *
 * The shell store patches only `StageStatus.state` as `job://stage` events
 * arrive — `startedIso` / `finishedIso` / `durationSec` stay null until the
 * backend reconciles them, which for a live run is never. The Jobs table needs
 * Started / Finished / Runtime columns now, so this module watches the store's
 * job for state transitions and stamps the clock itself.
 *
 * It observes `appStore` rather than the `job://*` events: those belong to W-C,
 * and subscribing twice would be a second source of truth.
 *
 * Kept per job, because a batch can have two subjects in flight and one set of
 * timings between them would score each run's progress against the other's
 * stage durations — which on a machine where one run is half a minute ahead
 * reads as a percentage that goes backwards.
 */

import { create } from "zustand";
import type { JobStatus, Modality, StageId } from "@/ipc/contract";
import { useAppStore } from "@/store/appStore";

export interface StageTiming {
  startedMs: number | null;
  finishedMs: number | null;
}

/** Keyed `${modality}:${stageId}`. */
export type StageTimings = Record<string, StageTiming>;

interface TimingState {
  /** The job whose timings `timings` exposes: the one in focus. */
  jobId: string | null;
  timings: StageTimings;
  /** Every run this session has watched, by job id. */
  byJob: Record<string, StageTimings>;
}

export const useStageTimings = create<TimingState>(() => ({
  jobId: null,
  timings: {},
  byJob: {},
}));

/** One run's timings, for a sibling row that is not the one in focus. */
export function timingsFor(jobId: string): StageTimings {
  return useStageTimings.getState().byJob[jobId] ?? {};
}

export function timingKey(modality: Modality, id: StageId): string {
  return `${modality}:${id}`;
}

/**
 * Forget everything. Called when a run starts; harmless with a batch in flight
 * because `absorb` rebuilds each job's entry from the stage states the store
 * already holds.
 */
export function resetStageTimings(): void {
  useStageTimings.setState({ jobId: null, timings: {}, byJob: {} });
}

const TERMINAL = new Set(["completed", "failed", "cancelled", "skipped"]);

function absorb(job: JobStatus): void {
  const now = Date.now();
  const state = useStageTimings.getState();
  const known = state.byJob[job.jobId];
  const fresh = known === undefined;
  const next: StageTimings = fresh ? {} : { ...known };
  let changed = fresh;

  for (const stage of job.stages) {
    const key = timingKey(stage.modality, stage.id);
    const current = next[key] ?? { startedMs: null, finishedMs: null };
    let startedMs = current.startedMs;
    let finishedMs = current.finishedMs;

    // Prefer the backend's timestamps when it supplies them.
    if (stage.startedIso) startedMs = new Date(stage.startedIso).getTime();
    else if (stage.state === "running" && startedMs == null) startedMs = now;

    if (stage.finishedIso) finishedMs = new Date(stage.finishedIso).getTime();
    else if (TERMINAL.has(stage.state) && startedMs != null && finishedMs == null) finishedMs = now;

    if (startedMs !== current.startedMs || finishedMs !== current.finishedMs) {
      next[key] = { startedMs, finishedMs };
      changed = true;
    } else if (fresh) {
      next[key] = current;
    }
  }

  // `timings` follows the job in focus, so the twenty-odd readers of it keep
  // working without knowing that several runs exist. Only the focus may move
  // that pointer: letting every absorbed job set it made two in-flight runs
  // hand it back and forth on every store update.
  const isFocus = useAppStore.getState().job?.jobId === job.jobId;
  const pointerStale = isFocus && state.jobId !== job.jobId;
  if (!changed && !pointerStale) return;
  useStageTimings.setState({
    byJob: { ...state.byJob, [job.jobId]: next },
    ...(isFocus ? { jobId: job.jobId, timings: next } : {}),
  });
}

let started = false;

/** Idempotent; safe under StrictMode double-mount. */
export function ensureStageTimings(): void {
  if (started) return;
  started = true;
  const initial = useAppStore.getState().jobs;
  for (const job of Object.values(initial)) absorb(job);
  useAppStore.subscribe((state, prev) => {
    // Every run, not only the one in focus: a sibling's stages have to be
    // stamped as they land or its row has no clock of its own.
    for (const job of Object.values(state.jobs)) {
      if (job !== prev.jobs[job.jobId]) absorb(job);
    }
    // The focus may have moved to a job whose timings are already known.
    const focus = state.job;
    if (focus && useStageTimings.getState().jobId !== focus.jobId) absorb(focus);
  });
}

export function stageTiming(key: string): StageTiming {
  return useStageTimings.getState().timings[key] ?? { startedMs: null, finishedMs: null };
}
